import React, { useEffect, useMemo, useState } from "react"
import {
  HeartIcon,
  ArrowPathIcon,
  LinkIcon,
  MegaphoneIcon,
  UserPlusIcon,
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  CubeIcon,
} from "@heroicons/react/24/outline"
import {
  useCustomAttributionControllerGetAllMediaTargetsQuery,
  useCustomAttributionControllerGetAllModelsQuery,
  CustomAttributionModelWithWeights,
} from "~/ui-rtk/api/customAttributionApi"
import { InitiativeType } from "~/ui-rtk/api/types/custom-attribution-enums"
import { getInitiativeTypeChartLabel } from "~/ui-rtk/constants/initiative-types"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import { polyfillCubeQueryWithTimeDimension } from "~/ui-rtk/utils/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import PieChart from "~/ui-rtk/components/ui/charts/PieChart"
import {
  CONNECTORS_IDS,
  CONNECTORS_IDS_GROUPED,
} from "~/ui-rtk/constants/sources"
import { DEP_VAR_SOURCES } from "~/ui-rtk/constants/revenue"
import useCurrentCompanyMetadata from "~/ui-rtk/hooks/current-company-metadata"
import { TableVisualization } from "~/ui-rtk/components/ui/charts"
import type {
  TMetric,
  TableVisualizationColumn,
} from "~/ui-rtk/constants/table-visualization-types"
import type { PaginationState } from "@tanstack/react-table"
import {
  calculateModeledRevenueForRow,
  calculateBrandValueForRow,
} from "~/ui-rtk/utils/calculateModeledRevenue"
import TabsGroup from "~/ui-rtk/components/ui/controls/TabsGroup/TabsGroup"

// Media target from RTK Query
interface MediaTargetRow {
  campaign_id?: string
  ad_set_id?: string
  initiativeType?: string
  [key: string]: unknown
}

// Processed row: cube row + computed fields
interface ProcessedRow {
  initiativeType: string
  modeledRevenue: number
  nextModeledRevenue: number
  model: CustomAttributionModelWithWeights | undefined
  adChannel: string | null
  [key: string]: unknown
}

// Row type for render functions (TMetric = Record<string, unknown>)
type RenderRow = Record<string, unknown>

export function RevenueBreakdownContainer({
  dateRange,
  adsetData,
  isAdsetDataLoading,
}: {
  dateRange: [Date?, Date?]
  adsetData?: Record<string, unknown>[]
  isAdsetDataLoading?: boolean
}) {
  const currentCompany = useCurrentCompany()
  const currentCompanyMetadata = useCurrentCompanyMetadata()

  // Add state for platform/initiative breakdown data
  // Remove useState for platformInitiativeData

  // Add state for revenue types data (baseline, triggered, paid)
  const [revenueTypesProcessedData, setRevenueTypesProcessedData] = useState<{
    baselineRevenue: number
    triggeredRevenue: number
    paidRevenue: number
    newPaidRevenue: number
    newBaselineRevenue: number
    newTriggeredRevenue: number
  } | null>(null)

  const [revenueBreakdownPagination, setRevenueBreakdownPagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: 3,
    })

  const [platformInitiativePagination, setPlatformInitiativePagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: 11,
    })

  // Remove main tab selection state - we'll show all content sections

  // Add state for brand value breakdown tabs
  const [brandValueTab, setBrandValueTab] = useState<
    "30d" | "60d" | "90d" | "120d" | "150d" | "agg"
  >("30d")

  // Add pagination state for brand value table
  const [brandValuePagination, setBrandValuePagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    })

  // Fetch all media targets for the current company
  const { data: allMediaTargets = [], isLoading: isMediaTargetsLoading } =
    useCustomAttributionControllerGetAllMediaTargetsQuery()

  // Fetch all attribution models with weights for the current company
  const {
    data: allAttributionModels = [] as CustomAttributionModelWithWeights[],
    isLoading: isAttributionModelsLoading,
  } = useCustomAttributionControllerGetAllModelsQuery()

  const revenueTypesQuery: TCubeQuery = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1] || !currentCompany?.id) {
      return null as unknown as TCubeQuery
    }

    const queryPrefix = "metrics"

    const baseQuery: Record<string, unknown> = {
      measures: [
        // Shopify revenue measures
        `${queryPrefix}.shopify_baseline_plus_paid_brand_search_revenue`,
        `${queryPrefix}.shopify_triggered_revenue`,
        `${queryPrefix}.shopify_paid_revenue`,

        // GA revenue measures
        `${queryPrefix}.ga_baseline_revenue`,
        `${queryPrefix}.ga_triggered_revenue`,
        `${queryPrefix}.ga_paid_revenue`,
      ],
      dimensions: [`${queryPrefix}.date`, `${queryPrefix}.companyid`],
      timeDimensions: [
        {
          dimension: `${queryPrefix}.date`,
        },
      ],
      filters: [
        {
          member: `${queryPrefix}.companyid`,
          operator: "equals",
          values: [currentCompany.id],
        },
      ],
    }

    // Apply date range
    return polyfillCubeQueryWithTimeDimension(baseQuery as TCubeQuery, {
      dateRange: [dateRange[0], dateRange[1]] as [Date, Date],
    })
  }, [dateRange?.[0]?.getTime(), dateRange?.[1]?.getTime(), currentCompany?.id])

  // Use passed adsetData instead of cube query
  const resultSet = useMemo(() => {
    if (!adsetData) return null
    return {
      rawData: () => adsetData,
    }
  }, [adsetData])

  const isCubeLoading = isAdsetDataLoading || false

  const { resultSet: revenueTypesResultSet, isLoading: isRevenueTypesLoading } =
    useCubeQuery(revenueTypesQuery, {
      skip:
        !revenueTypesQuery ||
        !dateRange ||
        !dateRange[0] ||
        !dateRange[1] ||
        !currentCompany?.id,
    })

  // Stabilize allMediaTargets to prevent infinite loops
  const stableMediaTargets = useMemo(
    () => (allMediaTargets || []) as MediaTargetRow[],
    [allMediaTargets?.length],
  )

  // Process the cube result data to add calculated modeled revenue
  const processedResultData = useMemo(() => {
    if (!resultSet || !allAttributionModels.length) {
      return null
    }

    const rawData = resultSet.rawData() as Record<string, string>[]
    const result = rawData.map(row => {
      // Find initiative type for this row
      const campaignId = row["company_media_metrics_adsets.campaign_id"]
      const adsetId = row["company_media_metrics_adsets.adset_id"]
      let initiativeType = "Uncategorized"

      // Priority 1: Check for adset-level override
      if (adsetId && stableMediaTargets.length > 0) {
        const adsetTarget = stableMediaTargets.find(
          target => target.ad_set_id === adsetId && target.ad_set_id !== "",
        )
        if (adsetTarget) {
          initiativeType = adsetTarget.initiativeType as string
        }
      }

      // Priority 2: Check for campaign-level setting
      if (
        initiativeType === "Uncategorized" &&
        campaignId &&
        stableMediaTargets.length > 0
      ) {
        const campaignTarget = stableMediaTargets.find(
          target =>
            target.campaign_id === campaignId &&
            (target.ad_set_id === "" || !target.ad_set_id),
        )
        if (campaignTarget) {
          initiativeType = campaignTarget.initiativeType as string
        }
      }

      // Find the matching attribution model for this initiative type
      const mediaChannel =
        row["company_media_metrics_adsets.media_channel"] || ""
      const objective = row["company_media_metrics_adsets.objective"] || ""
      const optimizationGoal =
        row["company_media_metrics_adsets.optimization_goal"] || ""
      const getAdChannel = (mediaChannel: string): string | null => {
        if (mediaChannel?.toLowerCase().includes("meta")) {
          return "Meta"
        }
        if (mediaChannel?.toLowerCase().includes("tiktok")) {
          return "Tiktok"
        }
        if (mediaChannel?.toLowerCase().includes("pinterest")) {
          return "Pinterest"
        }
        if (mediaChannel?.toLowerCase().includes("google")) {
          return "Google Ads"
        }
        if (mediaChannel?.toLowerCase().includes("youtube")) {
          return "YouTube"
        }
        return null
      }
      const adChannel = getAdChannel(mediaChannel)
      const model = allAttributionModels.find(m => {
        const matches =
          String(m.initiativeType) === String(initiativeType) &&
          String(m.adChannel) === String(adChannel) &&
          (!objective || String(m.adChannelObjective) === String(objective)) &&
          (!optimizationGoal ||
            String(m.adChannelOptimizationGoal) === String(optimizationGoal))
        return matches
      }) as CustomAttributionModelWithWeights | undefined
      const modeledRevenue = parseFloat(
        row["company_media_metrics_adsets.modeled_revenue"] || "0",
      )
      let nextModeledRevenue = modeledRevenue
      if (model && model.weights && model.weights.length > 0) {
        const modelWithWeights = model
        nextModeledRevenue = calculateModeledRevenueForRow({
          rowData: row,
          model: modelWithWeights,
          adChannel,
        })
      }
      return {
        ...row,
        initiativeType,
        modeledRevenue,
        nextModeledRevenue,
        model, // Add model to the processed data
        adChannel, // Also add adChannel for reference
      } as ProcessedRow
    })
    return result
  }, [resultSet, allAttributionModels, stableMediaTargets])

  // Function to calculate modeled revenue based on attribution weights - DEPRECATED
  // Now using processedResultData instead

  // Process revenue types data from cube results
  useEffect(() => {
    if (revenueTypesResultSet) {
      const rawData = revenueTypesResultSet.rawData()

      if (!currentCompanyMetadata?.model_props?.ecom?.dep_var) {
        return
      }
      // For now, using a boolean to determine revenue source (GA vs Shopify)
      // Check if dep_var matches any connector in the SHOPIFY group
      const shopifyConnectorIds = CONNECTORS_IDS_GROUPED[CONNECTORS_IDS.SHOPIFY]
      const shopifyDepVars =
        shopifyConnectorIds
          ?.filter(connectorId => connectorId in DEP_VAR_SOURCES)
          ?.flatMap(connectorId => [
            DEP_VAR_SOURCES[connectorId].MAIN,
            DEP_VAR_SOURCES[connectorId].ALT,
          ]) ?? []
      const useShopifyRevenue = shopifyDepVars.includes(
        currentCompanyMetadata?.model_props?.ecom?.dep_var ?? "",
      )

      // Sum up the revenue values
      let baselineRevenue = 0
      let triggeredRevenue = 0
      let paidRevenue = 0

      rawData.forEach(row => {
        if (useShopifyRevenue) {
          const baseline = parseFloat(
            row["metrics.shopify_baseline_plus_paid_brand_search_revenue"] ||
              "0",
          )
          const triggered = parseFloat(
            row["metrics.shopify_triggered_revenue"] || "0",
          )
          const paid = parseFloat(row["metrics.shopify_paid_revenue"] || "0")

          // Add safety checks to prevent invalid array length errors
          if (isFinite(baseline)) {
            baselineRevenue += baseline
          }
          if (isFinite(triggered)) {
            triggeredRevenue += triggered
          }
          if (isFinite(paid)) {
            paidRevenue += paid
          }
        } else {
          const baseline = parseFloat(row["metrics.ga_baseline_revenue"] || "0")
          const triggered = parseFloat(
            row["metrics.ga_triggered_revenue"] || "0",
          )
          const paid = parseFloat(row["metrics.ga_paid_revenue"] || "0")

          // Add safety checks to prevent invalid array length errors
          if (isFinite(baseline)) {
            baselineRevenue += baseline
          }
          if (isFinite(triggered)) {
            triggeredRevenue += triggered
          }
          if (isFinite(paid)) {
            paidRevenue += paid
          }
        }
      })

      // Calculate new paid revenue from attribution model toolkit
      // For now, using a sample calculation - this should come from the attribution toolkit
      const newPaidRevenue = paidRevenue * 0.8 // Example: 80% of original paid revenue

      // Calculate redistribution amount
      const redistributionAmount = paidRevenue - newPaidRevenue

      // Calculate distribution percentages
      const totalBaselineTriggered = triggeredRevenue + baselineRevenue
      const triggeredPercentage =
        totalBaselineTriggered > 0
          ? triggeredRevenue / totalBaselineTriggered
          : 0
      const baselinePercentage =
        totalBaselineTriggered > 0
          ? baselineRevenue / totalBaselineTriggered
          : 0

      // Calculate new values
      const newTriggeredRevenue =
        redistributionAmount * triggeredPercentage + triggeredRevenue
      const newBaselineRevenue =
        redistributionAmount * baselinePercentage + baselineRevenue

      setRevenueTypesProcessedData({
        baselineRevenue,
        triggeredRevenue,
        paidRevenue,
        newPaidRevenue,
        newBaselineRevenue,
        newTriggeredRevenue,
      })
    }
  }, [revenueTypesResultSet])

  // Generate revenue types chart data from processed data
  const revenueTypesData = useMemo(() => {
    if (!revenueTypesProcessedData) {
      // Fallback to sample data if no real data is available
      return [
        { name: "New Paid Revenue", value: 45, fill: "#3B82F6" },
        { name: "New Triggered Revenue", value: 30, fill: "#10B981" },
        { name: "New Baseline Revenue", value: 25, fill: "#F59E0B" },
      ]
    }

    const { newBaselineRevenue, newTriggeredRevenue, newPaidRevenue } =
      revenueTypesProcessedData

    // Add safety checks for revenue values
    const safeNewBaselineRevenue = isFinite(newBaselineRevenue)
      ? newBaselineRevenue
      : 0
    const safeNewTriggeredRevenue = isFinite(newTriggeredRevenue)
      ? newTriggeredRevenue
      : 0
    const safeNewPaidRevenue = isFinite(newPaidRevenue) ? newPaidRevenue : 0

    const total =
      safeNewBaselineRevenue + safeNewTriggeredRevenue + safeNewPaidRevenue

    if (total === 0 || !isFinite(total)) {
      return []
    }

    const data = [
      {
        name: "New Paid Revenue",
        value: Math.round((safeNewPaidRevenue / total) * 100),
        fill: "#3B82F6",
      },
      {
        name: "New Triggered Revenue",
        value: Math.round((safeNewTriggeredRevenue / total) * 100),
        fill: "#10B981",
      },
      {
        name: "New Baseline Revenue",
        value: Math.round((safeNewBaselineRevenue / total) * 100),
        fill: "#F59E0B",
      },
    ].filter(item => item.value > 0 && isFinite(item.value))

    return data
  }, [
    revenueTypesProcessedData,
    currentCompanyMetadata?.model_props?.ecom?.dep_var,
  ])

  // Aggregate revenue, brand value, and platform/initiative data in a single useMemo
  const {
    revenueData,
    brandValueRawData,
    platformInitiativeData,
    brandValuePeriodData,
  } = useMemo(() => {
    if (!processedResultData) {
      return {
        revenueData: {},
        brandValueRawData: {},
        platformInitiativeData: [],
        brandValuePeriodData: [],
      }
    }
    const revenueByInitiativeType: Record<
      string,
      { value: number; count: number }
    > = {}
    const brandValueByInitiativeType: Record<
      string,
      { value: number; count: number }
    > = {}
    const platformInitiativeMap: Record<
      string,
      {
        initiativeType: string
        platform: string
        platformRevenue: number
        modeledRevenue: number // original
        nextModeledRevenue: number // new
      }
    > = {}

    // Add brand value period data map
    const brandValuePeriodMap: Record<
      string,
      {
        initiativeType: string
        platform: string
        brandValue30d: number
        brandValue60d: number
        brandValue90d: number
        brandValue120d: number
        brandValue150d: number
        brandValueAgg: number
        estimatedBrandValue30d: number
        estimatedBrandValue60d: number
        estimatedBrandValue90d: number
        estimatedBrandValue120d: number
        estimatedBrandValue150d: number
        estimatedBrandValue180d: number
        nextBrandValue30d: number
        nextBrandValue60d: number
        nextBrandValue90d: number
        nextBrandValue120d: number
        nextBrandValue150d: number
        nextBrandValueAgg: number
      }
    > = {}

    processedResultData.forEach(row => {
      const cubeData = row as unknown as Record<string, string>
      const mediaChannel =
        cubeData["company_media_metrics_adsets.media_channel"] || "Unknown"
      const purchases = parseFloat(
        cubeData["company_media_metrics_adsets.purchases"] || "0",
      )
      const brandValue = parseFloat(
        cubeData["company_media_metrics_adsets.brand_value_agg"] || "0",
      )
      if (!isFinite(purchases) || !isFinite(brandValue)) return
      const initiativeType = row.initiativeType
      const modeledRevenue = row.modeledRevenue
      const nextModeledRevenue = row.nextModeledRevenue

      // Revenue aggregation (with fallback)
      let revenueValue = 0
      if (isFinite(nextModeledRevenue) && nextModeledRevenue > 0) {
        revenueValue = nextModeledRevenue
      } else if (isFinite(modeledRevenue) && modeledRevenue > 0) {
        revenueValue = modeledRevenue
      } else if (isFinite(purchases) && purchases > 0) {
        revenueValue = purchases
      }
      if (!revenueByInitiativeType[initiativeType]) {
        revenueByInitiativeType[initiativeType] = { value: 0, count: 0 }
      }
      if (isFinite(revenueValue) && revenueValue > 0) {
        revenueByInitiativeType[initiativeType].value += revenueValue
        revenueByInitiativeType[initiativeType].count += 1
      }

      // Brand value aggregation (with fallback)
      let brandValueToUse = 0
      if (isFinite(brandValue) && brandValue > 0) {
        brandValueToUse = brandValue
      }
      if (!brandValueByInitiativeType[initiativeType]) {
        brandValueByInitiativeType[initiativeType] = { value: 0, count: 0 }
      }
      if (isFinite(brandValueToUse) && brandValueToUse > 0) {
        brandValueByInitiativeType[initiativeType].value += brandValueToUse
        brandValueByInitiativeType[initiativeType].count += 1
      }

      // Platform/initiative aggregation
      const platformInitiativeKey = `${mediaChannel}_${initiativeType}`
      if (!platformInitiativeMap[platformInitiativeKey]) {
        platformInitiativeMap[platformInitiativeKey] = {
          initiativeType,
          platform: mediaChannel,
          platformRevenue: 0,
          modeledRevenue: 0,
          nextModeledRevenue: 0,
        }
      }
      if (isFinite(purchases)) {
        platformInitiativeMap[platformInitiativeKey].platformRevenue +=
          purchases
      }
      if (isFinite(modeledRevenue)) {
        platformInitiativeMap[platformInitiativeKey].modeledRevenue +=
          modeledRevenue
      }
      if (isFinite(nextModeledRevenue)) {
        platformInitiativeMap[platformInitiativeKey].nextModeledRevenue +=
          nextModeledRevenue
      }

      // Brand value period data aggregation using correct metric names
      const brandValue30d = parseFloat(
        cubeData["company_media_metrics_adsets.d30_brand_value"] || "0",
      )
      const brandValue60d = parseFloat(
        cubeData["company_media_metrics_adsets.d60_brand_value"] || "0",
      )
      const brandValue90d = parseFloat(
        cubeData["company_media_metrics_adsets.d90_brand_value"] || "0",
      )
      const brandValue120d = parseFloat(
        cubeData["company_media_metrics_adsets.d120_brand_value"] || "0",
      )
      const brandValue150d = parseFloat(
        cubeData["company_media_metrics_adsets.d150_brand_value"] || "0",
      )
      const brandValueAgg = parseFloat(
        cubeData["company_media_metrics_adsets.brand_value_agg"] || "0",
      )

      // Estimated brand values using correct metric names
      const estimatedBrandValue30d = parseFloat(
        cubeData["company_media_metrics_adsets.estimated_d30_brand_value"] ||
          "0",
      )
      const estimatedBrandValue60d = parseFloat(
        cubeData["company_media_metrics_adsets.estimated_d60_brand_value"] ||
          "0",
      )
      const estimatedBrandValue90d = parseFloat(
        cubeData["company_media_metrics_adsets.estimated_d90_brand_value"] ||
          "0",
      )
      const estimatedBrandValue120d = parseFloat(
        cubeData["company_media_metrics_adsets.estimated_d120_brand_value"] ||
          "0",
      )
      const estimatedBrandValue150d = parseFloat(
        cubeData["company_media_metrics_adsets.estimated_d150_brand_value"] ||
          "0",
      )
      const estimatedBrandValue180d = parseFloat(
        cubeData["company_media_metrics_adsets.estimated_d180_brand_value"] ||
          "0",
      )

      // Calculate brand values using the new calculation function (similar to calculateModeledRevenueForRow)
      const brandValue30dCalc = calculateBrandValueForRow({
        rowData: cubeData,
        model: row.model,
        period: "30d",
      })
      const brandValue60dCalc = calculateBrandValueForRow({
        rowData: cubeData,
        model: row.model,
        period: "60d",
      })
      const brandValue90dCalc = calculateBrandValueForRow({
        rowData: cubeData,
        model: row.model,
        period: "90d",
      })
      const brandValue120dCalc = calculateBrandValueForRow({
        rowData: cubeData,
        model: row.model,
        period: "120d",
      })
      const brandValue150dCalc = calculateBrandValueForRow({
        rowData: cubeData,
        model: row.model,
        period: "150d",
      })
      const brandValueAggCalc = calculateBrandValueForRow({
        rowData: cubeData,
        model: row.model,
        period: "agg",
      })

      // Use calculated values
      const nextBrandValue30d = brandValue30dCalc.next
      const nextBrandValue60d = brandValue60dCalc.next
      const nextBrandValue90d = brandValue90dCalc.next
      const nextBrandValue120d = brandValue120dCalc.next
      const nextBrandValue150d = brandValue150dCalc.next
      const nextBrandValueAgg = brandValueAggCalc.next

      const brandValuePeriodKey = `${mediaChannel}_${initiativeType}`
      if (!brandValuePeriodMap[brandValuePeriodKey]) {
        brandValuePeriodMap[brandValuePeriodKey] = {
          initiativeType,
          platform: mediaChannel,
          brandValue30d: 0,
          brandValue60d: 0,
          brandValue90d: 0,
          brandValue120d: 0,
          brandValue150d: 0,
          brandValueAgg: 0,
          estimatedBrandValue30d: 0,
          estimatedBrandValue60d: 0,
          estimatedBrandValue90d: 0,
          estimatedBrandValue120d: 0,
          estimatedBrandValue150d: 0,
          estimatedBrandValue180d: 0,
          nextBrandValue30d: 0,
          nextBrandValue60d: 0,
          nextBrandValue90d: 0,
          nextBrandValue120d: 0,
          nextBrandValue150d: 0,
          nextBrandValueAgg: 0,
        }
      }

      // Aggregate all brand value fields
      if (isFinite(brandValue30d)) {
        brandValuePeriodMap[brandValuePeriodKey].brandValue30d += brandValue30d
      }
      if (isFinite(brandValue60d)) {
        brandValuePeriodMap[brandValuePeriodKey].brandValue60d += brandValue60d
      }
      if (isFinite(brandValue90d)) {
        brandValuePeriodMap[brandValuePeriodKey].brandValue90d += brandValue90d
      }
      if (isFinite(brandValue120d)) {
        brandValuePeriodMap[brandValuePeriodKey].brandValue120d +=
          brandValue120d
      }
      if (isFinite(brandValue150d)) {
        brandValuePeriodMap[brandValuePeriodKey].brandValue150d +=
          brandValue150d
      }
      if (isFinite(brandValueAgg)) {
        brandValuePeriodMap[brandValuePeriodKey].brandValueAgg += brandValueAgg
      }

      if (isFinite(estimatedBrandValue30d)) {
        brandValuePeriodMap[brandValuePeriodKey].estimatedBrandValue30d +=
          estimatedBrandValue30d
      }
      if (isFinite(estimatedBrandValue60d)) {
        brandValuePeriodMap[brandValuePeriodKey].estimatedBrandValue60d +=
          estimatedBrandValue60d
      }
      if (isFinite(estimatedBrandValue90d)) {
        brandValuePeriodMap[brandValuePeriodKey].estimatedBrandValue90d +=
          estimatedBrandValue90d
      }
      if (isFinite(estimatedBrandValue120d)) {
        brandValuePeriodMap[brandValuePeriodKey].estimatedBrandValue120d +=
          estimatedBrandValue120d
      }
      if (isFinite(estimatedBrandValue150d)) {
        brandValuePeriodMap[brandValuePeriodKey].estimatedBrandValue150d +=
          estimatedBrandValue150d
      }
      if (isFinite(estimatedBrandValue180d)) {
        brandValuePeriodMap[brandValuePeriodKey].estimatedBrandValue180d +=
          estimatedBrandValue180d
      }

      if (isFinite(nextBrandValue30d)) {
        brandValuePeriodMap[brandValuePeriodKey].nextBrandValue30d +=
          nextBrandValue30d
      }
      if (isFinite(nextBrandValue60d)) {
        brandValuePeriodMap[brandValuePeriodKey].nextBrandValue60d +=
          nextBrandValue60d
      }
      if (isFinite(nextBrandValue90d)) {
        brandValuePeriodMap[brandValuePeriodKey].nextBrandValue90d +=
          nextBrandValue90d
      }
      if (isFinite(nextBrandValue120d)) {
        brandValuePeriodMap[brandValuePeriodKey].nextBrandValue120d +=
          nextBrandValue120d
      }
      if (isFinite(nextBrandValue150d)) {
        brandValuePeriodMap[brandValuePeriodKey].nextBrandValue150d +=
          nextBrandValue150d
      }
      if (isFinite(nextBrandValueAgg)) {
        brandValuePeriodMap[brandValuePeriodKey].nextBrandValueAgg +=
          nextBrandValueAgg
      }
    })
    return {
      revenueData: revenueByInitiativeType,
      brandValueRawData: brandValueByInitiativeType,
      platformInitiativeData: Object.values(platformInitiativeMap),
      brandValuePeriodData: Object.values(brandValuePeriodMap),
    }
  }, [processedResultData])

  // Helper function to get initiative type config
  const getInitiativeTypeConfig = (type: string) => {
    const configs: Record<
      string,
      { icon: React.ReactNode; fillColor: string; label: string }
    > = {
      [InitiativeType.RETENTION]: {
        icon: <HeartIcon />,
        fillColor: "#599F5F",
        label: getInitiativeTypeChartLabel(InitiativeType.RETENTION),
      },
      [InitiativeType.DEMAND_GENERATION]: {
        icon: <MegaphoneIcon />,
        fillColor: "#66DECD",
        label: getInitiativeTypeChartLabel(InitiativeType.DEMAND_GENERATION),
      },
      [InitiativeType.DEMAND_GENERATION_2]: {
        icon: <MegaphoneIcon />,
        fillColor: "#C33FA3",
        label: getInitiativeTypeChartLabel(InitiativeType.DEMAND_GENERATION_2),
      },
      [InitiativeType.DEMAND_CAPTURE_ACQUISITION]: {
        icon: <UserPlusIcon />,
        fillColor: "#5C8BD6",
        label: getInitiativeTypeChartLabel(
          InitiativeType.DEMAND_CAPTURE_ACQUISITION,
        ),
      },
      [InitiativeType.DEMAND_CAPTURE_RETARGETING]: {
        icon: <ArrowPathIcon />,
        fillColor: "#FB6514",
        label: getInitiativeTypeChartLabel(
          InitiativeType.DEMAND_CAPTURE_RETARGETING,
        ),
      },
      [InitiativeType.DEMAND_CAPTURE_BROAD]: {
        icon: <LinkIcon />,
        fillColor: "#F4D148",
        label: getInitiativeTypeChartLabel(InitiativeType.DEMAND_CAPTURE_BROAD),
      },
      [InitiativeType.BRANDED_SEARCH]: {
        icon: <MagnifyingGlassIcon />,
        fillColor: "#6366F1",
        label: getInitiativeTypeChartLabel(InitiativeType.BRANDED_SEARCH),
      },
      [InitiativeType.NON_BRAND_SEARCH]: {
        icon: <MagnifyingGlassIcon />,
        fillColor: "#8B5CF6",
        label: getInitiativeTypeChartLabel(InitiativeType.NON_BRAND_SEARCH),
      },
      [InitiativeType.PMAX]: {
        icon: <CubeIcon />,
        fillColor: "#D946EF",
        label: getInitiativeTypeChartLabel(InitiativeType.PMAX),
      },
      Uncategorized: {
        icon: <QuestionMarkCircleIcon />,
        fillColor: "#677489",
        label: "Uncategorized",
      },
    }
    return configs[type] || { icon: null, fillColor: "#8B5CF6", label: type }
  }

  // Generate chart data from real revenue data
  const campaignTypeData = useMemo(() => {
    if (!revenueData) {
      // Fallback to sample data if no real data is available
      return [
        {
          name: "Demand Generation",
          value: 35,
          fill: "#66DECD",
          icon: <MegaphoneIcon />,
        },
        {
          name: "Demand Capture\nAcquisition",
          value: 28,
          fill: "#5C8BD6",
          icon: <UserPlusIcon />,
        },
        {
          name: "Demand Capture\nRetargeting",
          value: 22,
          fill: "#FB6514",
          icon: <ArrowPathIcon />,
        },
        {
          name: "Retention",
          value: 11,
          fill: "#599F5F",
          icon: <HeartIcon />,
        },
        {
          name: "Demand Capture\nBroad",
          value: 4,
          fill: "#F4D148",
          icon: <LinkIcon />,
        },
      ]
    }

    // Calculate total revenue for percentage calculation
    const totalRevenue = Object.values(revenueData).reduce(
      (sum: number, item: { value: number }) => sum + item.value,
      0,
    )

    if (totalRevenue === 0) {
      return []
    }

    // Convert to chart data
    const data = Object.entries(revenueData)
      .filter(
        ([, data]: [string, { value: number; count: number }]) =>
          data.value > 0,
      )
      .map(
        ([initiativeType, data]: [
          string,
          { value: number; count: number },
        ]) => {
          const config = getInitiativeTypeConfig(initiativeType)
          const percentage = (data.value / totalRevenue) * 100

          return {
            name: config.label,
            value: Math.round(percentage),
            fill: config.fillColor,
            icon: config.icon,
            revenue: data.value,
            count: data.count,
          }
        },
      )
      .sort((a, b) => b.value - a.value) // Sort by percentage descending
    return data
  }, [revenueData])

  // Generate brand value chart data
  const brandValueData = useMemo(() => {
    if (!brandValueRawData) {
      return []
    }

    // Calculate total brand value for percentage calculation
    const totalBrandValue = Object.values(brandValueRawData).reduce(
      (sum: number, item: { value: number }) => sum + item.value,
      0,
    )

    if (totalBrandValue === 0) {
      return []
    }

    // Convert to chart data
    return Object.entries(brandValueRawData)
      .filter(
        ([, data]: [string, { value: number; count: number }]) =>
          data.value > 0,
      )
      .map(
        ([initiativeType, data]: [
          string,
          { value: number; count: number },
        ]) => {
          const config = getInitiativeTypeConfig(initiativeType)
          const percentage = (data.value / totalBrandValue) * 100

          return {
            name: config.label,
            value: Math.round(percentage),
            fill: config.fillColor,
            icon: config.icon,
            brandValue: data.value,
            count: data.count,
          }
        },
      )
      .sort((a, b) => b.value - a.value) // Sort by percentage descending
  }, [brandValueRawData])

  const isLoading =
    isMediaTargetsLoading ||
    isCubeLoading ||
    isRevenueTypesLoading ||
    isAttributionModelsLoading

  // Early return for loading or missing required data
  if (!currentCompany?.id) {
    return (
      <div className="bg-white/5 rounded-lg border border-system-blue-600/20 p-system-lg">
        <h2 className="text-white text-lg font-semibold mb-system-md">
          Last 30 Days Breakdown
        </h2>
        <div className="text-white/60 text-center py-8">
          No company data available
        </div>
      </div>
    )
  }

  if (!dateRange || !dateRange[0] || !dateRange[1]) {
    return (
      <div className="bg-white/5 rounded-lg border border-system-blue-600/20 p-system-lg">
        <h2 className="text-white text-lg font-semibold mb-system-md">
          Last 30 Days Breakdown
        </h2>
        <div className="text-white/60 text-center py-8">
          Please select a date range
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="bg-white/5 rounded-lg border border-system-blue-600/20 p-system-lg">
        <h2 className="text-white text-lg font-semibold mb-system-md">
          Last 30 Days Breakdown
        </h2>
        <div className="text-white/60 text-center py-8">
          Loading revenue breakdown data...
        </div>
      </div>
    )
  }
  const renderPieCharts = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-system-lg outline-none">
      {/* Revenue Types Breakdown */}
      <div className="bg-system-gray-800/30 border border-system-gray-700/50 rounded-lg p-system-md">
        <h4 className="text-lg font-semibold text-white mb-system-md text-center">
          Revenue Types Breakdown
        </h4>
        <div className="flex justify-center h-[600px]">
          <PieChart data={revenueTypesData} />
        </div>
      </div>

      {/* Revenue by Campaign Type */}
      <div className="bg-system-gray-800/30 border border-system-gray-700/50 rounded-lg p-system-md">
        <h4 className="text-lg font-semibold text-white mb-system-md text-center">
          Revenue by Campaign Type
        </h4>
        <div className="flex justify-center h-[600px]">
          {isLoading ? (
            <div className="text-system-gray-400">Loading...</div>
          ) : (
            <PieChart data={campaignTypeData} />
          )}
        </div>
      </div>

      {/* Revenue by Brand Value */}
      <div className="bg-system-gray-800/30 border border-system-gray-700/50 rounded-lg p-system-md">
        <h4 className="text-lg font-semibold text-white mb-system-md text-center">
          Revenue by Brand Value
        </h4>
        <div className="flex justify-center h-[600px]">
          {isLoading ? (
            <div className="text-system-gray-400">Loading...</div>
          ) : (
            <PieChart data={brandValueData} />
          )}
        </div>
      </div>
    </div>
  )

  const renderRevenueBreakdownTable = () => {
    if (!revenueTypesProcessedData) {
      return (
        <div className="text-white/60 text-center py-8">
          No revenue breakdown data available
        </div>
      )
    }

    const {
      baselineRevenue = 0,
      triggeredRevenue = 0,
      paidRevenue = 0,
      newBaselineRevenue = 0,
      newTriggeredRevenue = 0,
      newPaidRevenue = 0,
    } = revenueTypesProcessedData ?? {}

    // Transform data for TableVisualization
    const tableData: TMetric[] = [
      {
        breakdown: "Baseline",
        lastClick: isFinite(baselineRevenue) ? baselineRevenue : 0,
        modeledRevenue: isFinite(newBaselineRevenue) ? newBaselineRevenue : 0,
        changeValue: isFinite(newBaselineRevenue) ? newBaselineRevenue : 0,
        originalValue: isFinite(baselineRevenue) ? baselineRevenue : 0,
      },
      {
        breakdown: "Triggered",
        lastClick: isFinite(triggeredRevenue) ? triggeredRevenue : 0,
        modeledRevenue: isFinite(newTriggeredRevenue) ? newTriggeredRevenue : 0,
        changeValue: isFinite(newTriggeredRevenue) ? newTriggeredRevenue : 0,
        originalValue: isFinite(triggeredRevenue) ? triggeredRevenue : 0,
      },
      {
        breakdown: "Paid",
        lastClick: isFinite(paidRevenue) ? paidRevenue : 0,
        modeledRevenue: isFinite(newPaidRevenue) ? newPaidRevenue : 0,
        changeValue: isFinite(newPaidRevenue) ? newPaidRevenue : 0,
        originalValue: isFinite(paidRevenue) ? paidRevenue : 0,
      },
    ]

    // Column configuration for TableVisualization
    const columnConfig: TableVisualizationColumn[] = [
      {
        key: "breakdown",
        header: "Breakdown",
        type: "title",
        width: 150,
      },
      {
        key: "lastClick",
        header: "Last Click",
        type: "currency",
      },
      {
        key: "modeledRevenue",
        header: "Modeled Revenue",
        type: "currency",
      },
      {
        key: "change",
        header: "Change",
        type: "text",
        render: (row: RenderRow) => {
          const newValue = row.changeValue as number
          const originalValue = row.originalValue as number

          if (originalValue === 0) {
            return <span className="text-white/60">N/A</span>
          }

          const percentageChange =
            ((newValue - originalValue) / originalValue) * 100
          const sign = percentageChange >= 0 ? "+" : ""
          const colorClass =
            percentageChange >= 0 ? "text-green-400" : "text-red-400"

          return (
            <span className={colorClass}>
              {sign}
              {percentageChange.toFixed(1)}%
            </span>
          )
        },
      },
    ]

    return (
      <div className="mt-system-lg">
        <h3 className="text-white text-md font-medium mb-system-sm">
          Revenue Breakdown Details
        </h3>
        <TableVisualization
          metrics={tableData ?? []}
          columnConfig={columnConfig}
          chartId="revenue-breakdown-details"
          paginationState={revenueBreakdownPagination}
          setPaginationState={setRevenueBreakdownPagination}
          enablePagination={true}
          hideItemsPerPage={true}
          className="border border-system-blue-600/20"
        />
      </div>
    )
  }

  const renderPlatformInitiativeTable = () => {
    // if (!platformInitiativeData || platformInitiativeData.length === 0) {
    //   return (
    //     <div className="text-white/60 text-center py-8">
    //       No platform initiative data available
    //     </div>
    //   )
    // }

    // Sort data to push "Uncategorized" to the bottom
    const sortedPlatformInitiativeData =
      platformInitiativeData?.slice().sort((a, b) => {
        // If one is uncategorized and the other isn't, put uncategorized last
        if (
          a.initiativeType === "Uncategorized" &&
          b.initiativeType !== "Uncategorized"
        ) {
          return 1
        }
        if (
          a.initiativeType !== "Uncategorized" &&
          b.initiativeType === "Uncategorized"
        ) {
          return -1
        }
        // Otherwise, sort alphabetically by initiative type, then by platform
        if (a.initiativeType !== b.initiativeType) {
          return a.initiativeType.localeCompare(b.initiativeType)
        }
        return a.platform.localeCompare(b.platform)
      }) || []

    // Transform data for TableVisualization
    const tableData: TMetric[] = sortedPlatformInitiativeData.map(
      (item, index) => {
        // Add safety checks for all numeric values
        const safePlatformRevenue = isFinite(item.platformRevenue)
          ? item.platformRevenue
          : 0
        const safeModeledRevenue = isFinite(item.modeledRevenue)
          ? item.modeledRevenue
          : 0
        const safeNextModeledRevenue = isFinite(item.nextModeledRevenue)
          ? item.nextModeledRevenue
          : 0
        const diffPurchasesVsNext = safeNextModeledRevenue - safePlatformRevenue
        const diffModeledVsNext = safeNextModeledRevenue - safeModeledRevenue
        const diffPercentPurchasesVsNext =
          safePlatformRevenue !== 0
            ? ((safeNextModeledRevenue - safePlatformRevenue) /
                safePlatformRevenue) *
              100
            : null
        const diffPercentModeledVsNext =
          safeModeledRevenue !== 0
            ? ((safeNextModeledRevenue - safeModeledRevenue) /
                safeModeledRevenue) *
              100
            : null

        return {
          id: `${item.platform}_${item.initiativeType}_${index}`,
          initiativeType: item.initiativeType || "Unknown",
          platform: item.platform || "Unknown",
          platformRevenue: safePlatformRevenue,
          modeledRevenue: safeModeledRevenue, // original
          nextModeledRevenue: safeNextModeledRevenue, // new
          diffPurchasesVsNext,
          diffModeledVsNext,
          diffPercentPurchasesVsNext,
          diffPercentModeledVsNext,
        }
      },
    )

    // Column configuration for TableVisualization
    const columnConfig: TableVisualizationColumn[] = [
      {
        key: "initiativeType",
        header: "Initiative Type",
        type: "title",
        width: 180,
      },
      {
        key: "platform",
        header: "Platform",
        type: "title",
        width: 120,
      },
      {
        key: "platformRevenue",
        header: "Platform Revenue",
        type: "currency",
      },
      {
        key: "modeledRevenue",
        header: "Modeled Revenue (Original)",
        type: "currency",
      },
      {
        key: "nextModeledRevenue",
        header: "Next Modeled Revenue",
        type: "currency",
      },
      {
        key: "diffPurchasesVsNext",
        header: "(Platform Revenue vs Next Modeled Revenue)",
        type: "text",
        render: (row: RenderRow) => {
          const diff = row.diffPurchasesVsNext as number
          const sign = diff >= 0 ? "+" : ""
          const colorClass =
            diff >= 0 ? "text-system-success-400" : "text-system-error-400"
          const formatted = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(Math.abs(diff))
          return (
            <span className={colorClass}>
              {sign}
              {formatted}
            </span>
          )
        },
      },
      {
        key: "diffPercentPurchasesVsNext",
        header: "(Platform Revenue vs Next Modeled Revenue) %",
        type: "text",
        render: (row: RenderRow) => {
          const percent = row.diffPercentPurchasesVsNext as number | null
          if (percent === null) {
            return <span className="text-white/60">N/A</span>
          }
          const sign = percent >= 0 ? "+" : ""
          const colorClass =
            percent >= 0 ? "text-system-success-400" : "text-system-error-400"
          return (
            <span className={colorClass}>
              {sign}
              {percent.toFixed(1)}%
            </span>
          )
        },
      },
      {
        key: "diffModeledVsNext",
        header: "(Modeled Revenue vs Next Modeled Revenue)",
        type: "text",
        render: (row: RenderRow) => {
          const diff = row.diffModeledVsNext as number
          const sign = diff >= 0 ? "+" : ""
          const colorClass =
            diff >= 0 ? "text-system-success-400" : "text-system-error-400"
          const formatted = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(Math.abs(diff))
          return (
            <span className={colorClass}>
              {sign}
              {formatted}
            </span>
          )
        },
      },
      {
        key: "diffPercentModeledVsNext",
        header: "(Modeled Revenue vs Next Modeled Revenue) %",
        type: "text",
        render: (row: RenderRow) => {
          const percent = row.diffPercentModeledVsNext as number | null
          if (percent === null) {
            return <span className="text-white/60">N/A</span>
          }
          const sign = percent >= 0 ? "+" : ""
          const colorClass =
            percent >= 0 ? "text-system-success-400" : "text-system-error-400"
          return (
            <span className={colorClass}>
              {sign}
              {percent.toFixed(1)}%
            </span>
          )
        },
      },
    ]

    return (
      <div className="mt-system-lg">
        <h3 className="text-white text-md font-medium mb-system-sm">
          Platform & Initiative Type Breakdown
        </h3>
        <TableVisualization
          metrics={tableData ?? []}
          columnConfig={columnConfig}
          highlightCells={true}
          chartId="platform-initiative-breakdown"
          paginationState={platformInitiativePagination}
          setPaginationState={setPlatformInitiativePagination}
          enablePagination={true}
          hideItemsPerPage={false}
          className="border border-system-blue-600/20"
        />
      </div>
    )
  }

  const renderBrandValueBreakdownTable = () => {
    if (!brandValuePeriodData || brandValuePeriodData.length === 0) {
      return (
        <div className="text-white/60 text-center py-8">
          No brand value data available
        </div>
      )
    }

    // Sort data to push "Uncategorized" to the bottom
    const sortedData = brandValuePeriodData.slice().sort((a, b) => {
      if (
        a.initiativeType === "Uncategorized" &&
        b.initiativeType !== "Uncategorized"
      ) {
        return 1
      }
      if (
        a.initiativeType !== "Uncategorized" &&
        b.initiativeType === "Uncategorized"
      ) {
        return -1
      }
      if (a.initiativeType !== b.initiativeType) {
        return a.initiativeType.localeCompare(b.initiativeType)
      }
      return a.platform.localeCompare(b.platform)
    })

    // Get the field names for the selected period
    const getFieldNames = (period: string) => {
      switch (period) {
        case "30d":
          return {
            current: "brandValue30d",
            estimated: "estimatedBrandValue30d",
            next: "nextBrandValue30d",
          }
        case "60d":
          return {
            current: "brandValue60d",
            estimated: "estimatedBrandValue60d",
            next: "nextBrandValue60d",
          }
        case "90d":
          return {
            current: "brandValue90d",
            estimated: "estimatedBrandValue90d",
            next: "nextBrandValue90d",
          }
        case "120d":
          return {
            current: "brandValue120d",
            estimated: "estimatedBrandValue120d",
            next: "nextBrandValue120d",
          }
        case "150d":
          return {
            current: "brandValue150d",
            estimated: "estimatedBrandValue150d",
            next: "nextBrandValue150d",
          }
        case "agg":
          return {
            current: "brandValueAgg",
            estimated: "estimatedBrandValue180d", // Using 180d as estimated for agg
            next: "nextBrandValueAgg",
          }
        default:
          return {
            current: "brandValue30d",
            estimated: "estimatedBrandValue30d",
            next: "nextBrandValue30d",
          }
      }
    }

    const fieldNames = getFieldNames(brandValueTab)

    // Transform data for TableVisualization
    const tableData: TMetric[] = sortedData.map((item, index) => {
      // Add safety checks for all numeric values
      const currentValue = isFinite(
        (item as unknown as Record<string, number>)[fieldNames.current],
      )
        ? (item as unknown as Record<string, number>)[fieldNames.current]
        : 0
      const estimatedValue = isFinite(
        (item as unknown as Record<string, number>)[fieldNames.estimated],
      )
        ? (item as unknown as Record<string, number>)[fieldNames.estimated]
        : 0
      const nextValue = isFinite(
        (item as unknown as Record<string, number>)[fieldNames.next],
      )
        ? (item as unknown as Record<string, number>)[fieldNames.next]
        : 0

      const diffCurrentVsNext = nextValue - currentValue
      const diffEstimatedVsNext = nextValue - estimatedValue
      const diffPercentCurrentVsNext =
        currentValue !== 0
          ? ((nextValue - currentValue) / currentValue) * 100
          : null
      const diffPercentEstimatedVsNext =
        estimatedValue !== 0
          ? ((nextValue - estimatedValue) / estimatedValue) * 100
          : null

      return {
        id: `${item.platform}_${item.initiativeType}_${index}`,
        initiativeType: item.initiativeType || "Unknown",
        platform: item.platform || "Unknown",
        currentBrandValue: currentValue,
        estimatedBrandValue: estimatedValue,
        nextBrandValue: nextValue,
        diffCurrentVsNext,
        diffEstimatedVsNext,
        diffPercentCurrentVsNext,
        diffPercentEstimatedVsNext,
      }
    })

    // Helper function to get display label for headers
    const getDisplayLabel = (period: string) =>
      period === "agg" ? "Brand Value" : `${period.toUpperCase()} Brand Value`

    // Column configuration for TableVisualization
    const columnConfig: TableVisualizationColumn[] = [
      {
        key: "initiativeType",
        header: "Initiative Type",
        type: "title",
        width: 180,
      },
      {
        key: "platform",
        header: "Platform",
        type: "title",
        width: 120,
      },
      {
        key: "currentBrandValue",
        header: getDisplayLabel(brandValueTab),
        type: "currency",
      },
      {
        key: "estimatedBrandValue",
        header: `Estimated ${getDisplayLabel(brandValueTab)}`,
        type: "currency",
      },
      {
        key: "nextBrandValue",
        header: `Next ${getDisplayLabel(brandValueTab)}`,
        type: "currency",
      },
      {
        key: "diffCurrentVsNext",
        header: `(${getDisplayLabel(brandValueTab)} vs Next)`,
        type: "text",
        render: (row: RenderRow) => {
          const diff = row.diffCurrentVsNext as number
          const sign = diff >= 0 ? "+" : ""
          const colorClass =
            diff >= 0 ? "text-system-success-400" : "text-system-error-400"
          const formatted = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(Math.abs(diff))
          return (
            <span className={colorClass}>
              {sign}
              {formatted}
            </span>
          )
        },
      },
      {
        key: "diffPercentCurrentVsNext",
        header: `(${getDisplayLabel(brandValueTab)} vs Next) %`,
        type: "text",
        render: (row: RenderRow) => {
          const percent = row.diffPercentCurrentVsNext as number | null
          if (percent === null) {
            return <span className="text-white/60">N/A</span>
          }
          const sign = percent >= 0 ? "+" : ""
          const colorClass =
            percent >= 0 ? "text-system-success-400" : "text-system-error-400"
          return (
            <span className={colorClass}>
              {sign}
              {percent.toFixed(1)}%
            </span>
          )
        },
      },
      {
        key: "diffEstimatedVsNext",
        header: `(Estimated ${getDisplayLabel(brandValueTab)} vs Next)`,
        type: "text",
        render: (row: RenderRow) => {
          const diff = row.diffEstimatedVsNext as number
          const sign = diff >= 0 ? "+" : ""
          const colorClass =
            diff >= 0 ? "text-system-success-400" : "text-system-error-400"
          const formatted = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(Math.abs(diff))
          return (
            <span className={colorClass}>
              {sign}
              {formatted}
            </span>
          )
        },
      },
      {
        key: "diffPercentEstimatedVsNext",
        header: `(Estimated ${getDisplayLabel(brandValueTab)} vs Next) %`,
        type: "text",
        render: (row: RenderRow) => {
          const percent = row.diffPercentEstimatedVsNext as number | null
          if (percent === null) {
            return <span className="text-white/60">N/A</span>
          }
          const sign = percent >= 0 ? "+" : ""
          const colorClass =
            percent >= 0 ? "text-system-success-400" : "text-system-error-400"
          return (
            <span className={colorClass}>
              {sign}
              {percent.toFixed(1)}%
            </span>
          )
        },
      },
    ]

    // Brand value period tabs using TabsGroup
    const brandValuePeriods = [
      { id: "30d", label: "30D Brand Value" },
      { id: "60d", label: "60D Brand Value" },
      { id: "90d", label: "90D Brand Value" },
      { id: "120d", label: "120D Brand Value" },
      { id: "150d", label: "150D Brand Value" },
      { id: "agg", label: "Brand Value" },
    ] as const

    const brandValueTabs = (
      <div className="mb-system-md">
        <TabsGroup
          options={brandValuePeriods.map(period => ({
            label: period.label,
            id: period.id,
          }))}
          onChange={value => setBrandValueTab(brandValuePeriods[value].id)}
          index={brandValuePeriods.findIndex(
            period => period.id === brandValueTab,
          )}
        >
          {tab => (
            <div className="flex items-center gap-system-xs">
              <span>{tab.label}</span>
            </div>
          )}
        </TabsGroup>
      </div>
    )

    return (
      <div className="mt-system-lg">
        <h3 className="text-white text-md font-medium mb-system-sm">
          Brand Value Breakdown by Period
        </h3>
        {brandValueTabs}
        <TableVisualization
          metrics={tableData ?? []}
          columnConfig={columnConfig}
          highlightCells={true}
          chartId={`brand-value-${brandValueTab}-breakdown`}
          paginationState={brandValuePagination}
          setPaginationState={setBrandValuePagination}
          enablePagination={true}
          hideItemsPerPage={false}
          className="border border-system-blue-600/20"
        />
      </div>
    )
  }

  // Removed main tabs - content sections will be shown directly

  return (
    <div className="bg-white/5 rounded-lg border border-system-blue-600/20 p-system-lg">
      <h2 className="text-white text-lg font-semibold mb-system-md">
        Last 30 Days Breakdown
      </h2>
      {renderPieCharts()}
      {renderRevenueBreakdownTable()}
      {renderPlatformInitiativeTable()}
      {renderBrandValueBreakdownTable()}
    </div>
  )
}

export default RevenueBreakdownContainer
